import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { CustomOption } from '@repo/toolkit/types'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { getTimezones } from '../../../api/timezones/get-timezones'
import useTranslate from '../../../lib/hooks/use-translate'
import { IDTypeEnum, IDTypeEnumMapping } from '../../../lib/types/enums/common/id-type.enum'
import {
  VATOptionEnum,
  VATOptionEnumMapping
} from '../../../lib/types/enums/country/vat-option.enum'
import { CountryInput } from '../../../lib/types/models/country/country-input'
import { CountryKey, getCountryLabel } from '../../../lib/types/models/country/country-list'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import CountryFlag from '../../elements/country-flag'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import LoadingScreen from '../../elements/loading/loading-screen'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<CountryInput> = z.object({
  name: createStringSchema('NOM'),
  code: createStringSchema('code'),
  vat: createEnumSchema({ fieldName: 'TVA', enumType: VATOptionEnum }),
  displayToStaff: z.boolean().optional(),
  timezone: createStringSchema('Timezone'),
  position: createNumberSchema('position', { minimumValue: 1 }),
  requiredDocuments: createEnumArraySchema({
    enumType: IDTypeEnum,
    fieldName: 'Documents requis'
  }).default([])
})

const CountryForm = ({ defaultValues, ...validationButtonProps }: BaseFormProps<CountryInput>) => {
  const { t, currentLanguage } = useTranslate()
  const form = useForm<CountryInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const { data: timezones } = useQuery({
    queryKey: ['timezones'],
    queryFn: () => getTimezones()
  })

  const countrySelectOptions: CustomOption<string>[] = [
    {
      label: 'Angleterre',
      value: CountryKey.GBEA,
      custom: <CountryFlag code={CountryKey.GBEA} label={'Angleterre'} />
    },
    {
      label: 'Irlande',
      value: CountryKey.IRL,
      custom: <CountryFlag code={CountryKey.IRL} label={'Irlande'} />
    },
    {
      label: 'Canada',
      value: CountryKey.CAN,
      custom: <CountryFlag code={CountryKey.CAN} label={'Canada'} />
    },
    {
      label: 'USA',
      value: CountryKey.USA,
      custom: <CountryFlag code={CountryKey.USA} label={'USA'} />
    },
    {
      label: 'Allemagne',
      value: CountryKey.DEU,
      custom: <CountryFlag code={CountryKey.DEU} label={'Allemagne'} />
    },
    {
      label: 'Espagne',
      value: CountryKey.ESP,
      custom: <CountryFlag code={CountryKey.ESP} label={'Espagne'} />
    },
    {
      label: 'Ecosse',
      value: CountryKey.GBES,
      custom: <CountryFlag code={CountryKey.GBES} label={'Ecosse'} />
    },
    {
      label: 'France',
      value: CountryKey.FRA,
      custom: <CountryFlag code={CountryKey.FRA} label={'France'} />
    },
    {
      label: 'Australie',
      value: CountryKey.AUS,
      custom: <CountryFlag code={CountryKey.AUS} label={'Australie'} />
    },
    {
      label: 'Malte',
      value: CountryKey.MLT,
      custom: <CountryFlag code={CountryKey.MLT} label={'Malte'} />
    },
    {
      label: 'Grèce',
      value: CountryKey.GRC,
      custom: <CountryFlag code={CountryKey.GRC} label={'Grèce'} />
    }
  ]

  if (!countryList) return <LoadingScreen />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Pays
          </Typography>

          <div className={'grid grid-cols-2 gap-4'}>
            <FormField
              control={form.control}
              name={'code'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      searchable
                      required
                      {...field}
                      label={'NOM'}
                      options={countrySelectOptions}
                      selectedOption={conditionalRender(!!field?.value, {
                        value: field.value,
                        label:
                          getCountryLabel(
                            currentLanguage,
                            countryList.data.find(({ key }) => key === field.value)
                          ) ?? '',
                        custom: (
                          <CountryFlag
                            code={field.value}
                            label={getCountryLabel(
                              currentLanguage,
                              countryList.data.find(({ key }) => key === field.value)
                            )}
                          />
                        )
                      })}
                      onChange={(value) => {
                        form.setValue('code', value, { shouldValidate: true })
                        // Use the newly selected `value` (not the stale `field.value`) to derive the name
                        form.setValue(
                          'name',
                          getCountryLabel(
                            currentLanguage,
                            countryList.data.find(({ key }) => key === value)
                          ) ?? '',
                          { shouldValidate: true }
                        )
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'vat'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      label={'TVA'}
                      options={Object.values(VATOptionEnum).map((status) => ({
                        label: VATOptionEnumMapping[status],
                        value: status
                      }))}
                      selectedOption={conditionalRender(
                        !!field?.value,
                        getSelectedEnumOption(field.value, VATOptionEnumMapping)
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'timezone'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      searchable
                      label={'Timezone'}
                      options={timezones?.map((timezone) => ({
                        label: timezone.name,
                        value: timezone.name
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          timezones?.find((timezone) => timezone.name === field.value)?.name ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'position'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      type="number"
                      label={'Position'}
                      placeholder={'E.g. 1'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'requiredDocuments'}
              render={({ field }) => (
                <FormItem className={'col-span-2'}>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      label={'Documents requis'}
                      variant="primary"
                      options={Object.values(IDTypeEnum).map((type) => ({
                        label: t(IDTypeEnumMapping[type].title),
                        value: type
                      }))}
                      selectedOptions={field.value?.map((value) => ({
                        label: t(IDTypeEnumMapping[value].title),
                        value
                      }))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name={'displayToStaff'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    label={'Afficher aux ME'}
                    onChange={(checked) => field.onChange(checked)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default CountryForm
